import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { setAccessToken } from '../../utils/authTokenStore';

const backendUrl = import.meta.env.VITE_BACKEND_URL || '';
const REMEMBER_DEVICE_DAYS = 30;
const SUPPORT_EMAIL = 'service@qualz.ai';

function MfaChallengeDialog({ open, challengeToken, onClose, onSuccess }) {
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [loading, setLoading] = useState(false);

  const canSubmit = useRecovery ? recoveryCode.trim().length > 0 : code.trim().length > 0;

  const submit = async () => {
    if (!challengeToken || loading || !canSubmit) {
      return;
    }
    setLoading(true);
    try {
      const endpoint = useRecovery ? '/auth/mfa/recovery' : '/auth/mfa/verify';
      const payload = useRecovery
        ? {
            challenge_token: challengeToken,
            recovery_code: recoveryCode,
            remember_device: rememberDevice,
          }
        : {
            challenge_token: challengeToken,
            code,
            remember_device: rememberDevice,
          };
      const response = await axios.post(`${backendUrl}${endpoint}`, payload, {
        withCredentials: true,
      });
      const token = response.data?.access_token;
      if (!token) {
        throw new Error('Missing access token');
      }
      setAccessToken(token);
      onSuccess?.();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail?.message || detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  // Enter submits the code — pairs with the autofocused input so most
  // users never touch the mouse. Handled at form level rather than
  // per-input so recovery-mode gets the same behaviour for free.
  const handleSubmit = (evt) => {
    evt.preventDefault();
    submit();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>Two-factor authentication</DialogTitle>
      {/* Form wrapper so Enter submits without hunting for the Verify
          button — critical for the numeric-keypad flow. */}
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogContent sx={{ pt: 1, pb: 2, px: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            {useRecovery
              ? 'Enter one of your single-use recovery codes.'
              : 'Enter the 6-digit code from your authenticator app.'}
          </Typography>
          {/* key= remounts the input when the user toggles modes, so
              MUI re-runs autoFocus and moves the cursor into the newly
              shown field. Without the key, MUI only autofocuses on
              first mount and the toggle leaves the cursor stranded. */}
          {useRecovery ? (
            <TextField
              key="recovery"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- modal-dialog with a single primary input; ARIA APG recommends focusing it on open so keyboard/screen-reader users don't have to hunt.
              autoFocus
              fullWidth
              label="Recovery code"
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value)}
              disabled={loading}
            />
          ) : (
            <TextField
              key="code"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- see recovery-input note above; same rationale for the authenticator input.
              autoFocus
              fullWidth
              label="Authenticator code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
              inputProps={{ inputMode: 'numeric', maxLength: 8, autoComplete: 'one-time-code' }}
              disabled={loading}
            />
          )}
          <Box sx={{ mt: 2.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  disabled={loading}
                />
              }
              label={`Remember this device for ${REMEMBER_DEVICE_DAYS} days`}
            />
          </Box>
          <Box sx={{ mt: 0.5 }}>
            <Button
              size="small"
              onClick={() => setUseRecovery((prev) => !prev)}
              disabled={loading}
              sx={{ textTransform: 'none' }}
            >
              {useRecovery ? 'Use authenticator app instead' : 'Use a recovery code instead'}
            </Button>
          </Box>
          <Divider sx={{ mt: 3, mb: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Trouble with your authenticator app? Contact us at{' '}
            <Link href={`mailto:${SUPPORT_EMAIL}`} underline="hover">
              {SUPPORT_EMAIL}
            </Link>{' '}
            and we&rsquo;ll help you back in.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading || !canSubmit}>
            {loading ? 'Verifying…' : 'Verify'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default MfaChallengeDialog;
